(function () {
  const TZ = 'America/New_York';

  function getConfig() {
    const cfg = window.__shippingCountdownConfig || {};
    const dates = Array.isArray(cfg.nonShippingDates) ? cfg.nonShippingDates : [];
    return {
      cutoffHour: typeof cfg.cutoffHour === 'number' ? cfg.cutoffHour : 14,
      nonShippingDates: dates
        .map((d) => String(d).trim())
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)),
    };
  }

  function getEasternParts(date) {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'long',
      hour12: false,
    });
    const parts = {};
    for (const p of fmt.formatToParts(date)) {
      if (p.type !== 'literal') parts[p.type] = p.value;
    }
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second),
      weekday: parts.weekday,
    };
  }

  function dateKey(y, m, d) {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  function isWeekday(weekday) {
    return weekday !== 'Saturday' && weekday !== 'Sunday';
  }

  function buildHolidaySet(dates) {
    return new Set(dates);
  }

  function isShippingDayParts(parts, holidays) {
    return isWeekday(parts.weekday) && !holidays.has(dateKey(parts.year, parts.month, parts.day));
  }

  function addCalendarDays(y, m, d, days) {
    return getEasternParts(new Date(Date.UTC(y, m - 1, d + days, 12, 0, 0)));
  }

  function getNextShippingDayAfter(y, m, d, holidays) {
    for (let offset = 1; offset <= 366; offset += 1) {
      const candidate = addCalendarDays(y, m, d, offset);
      if (isShippingDayParts(candidate, holidays)) return candidate;
    }
    return null;
  }

  function getNextShippingDayOnOrAfter(y, m, d, holidays) {
    for (let offset = 0; offset <= 366; offset += 1) {
      const candidate = addCalendarDays(y, m, d, offset);
      if (isShippingDayParts(candidate, holidays)) return candidate;
    }
    return null;
  }

  function isBeforeCutoff(parts, cutoffHour) {
    const nowSec = parts.hour * 3600 + parts.minute * 60 + parts.second;
    return nowSec < cutoffHour * 3600;
  }

  function isFridayAfterCutoff(parts, cutoffHour, holidays) {
    if (parts.weekday !== 'Friday') return false;
    if (!isShippingDayParts(parts, holidays)) return false;
    return !isBeforeCutoff(parts, cutoffHour);
  }

  function isWeekendWindow(parts, cutoffHour, holidays) {
    if (parts.weekday === 'Saturday' || parts.weekday === 'Sunday') return true;
    return isFridayAfterCutoff(parts, cutoffHour, holidays);
  }

  function compareEastern(ms, y, m, d, hour, minute, second) {
    const p = getEasternParts(new Date(ms));
    if (p.year !== y) return p.year - y;
    if (p.month !== m) return p.month - m;
    if (p.day !== d) return p.day - d;
    if (p.hour !== hour) return p.hour - hour;
    if (p.minute !== minute) return p.minute - minute;
    return p.second - second;
  }

  function easternTimestamp(y, m, d, hour, minute, second) {
    let lo = Date.UTC(y, m - 1, d - 1, 0, 0, 0);
    let hi = Date.UTC(y, m - 1, d + 1, 23, 59, 59);
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (compareEastern(mid, y, m, d, hour, minute, second) < 0) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  function secondsUntilTarget(targetMs, now) {
    return Math.max(0, Math.floor((targetMs - now.getTime()) / 1000));
  }

  function formatCountdown(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h}h ${m}m ${s}s`;
  }

  function formatCutoffLabel(cutoffHour) {
    const period = cutoffHour >= 12 ? 'PM' : 'AM';
    const h = cutoffHour % 12 || 12;
    return `${h} ${period} EST`;
  }

  function getTargetMondayParts(parts) {
    const daysUntilMonday = {
      Friday: 3,
      Saturday: 2,
      Sunday: 1,
    };
    return addCalendarDays(parts.year, parts.month, parts.day, daysUntilMonday[parts.weekday]);
  }

  function buildCountdownMessage(now, config) {
    const holidays = buildHolidaySet(config.nonShippingDates);
    const cutoffHour = config.cutoffHour;
    const parts = getEasternParts(now);

    if (isShippingDayParts(parts, holidays) && isBeforeCutoff(parts, cutoffHour)) {
      const targetMs = easternTimestamp(parts.year, parts.month, parts.day, cutoffHour, 0, 0);
      const sec = secondsUntilTarget(targetMs, now);
      return `Ships today if ordered in the next <span class="green">${formatCountdown(sec)}</span>`;
    }

    if (isWeekendWindow(parts, cutoffHour, holidays)) {
      const monday = getTargetMondayParts(parts);
      const shipDay = getNextShippingDayOnOrAfter(monday.year, monday.month, monday.day, holidays);
      if (!shipDay) return '';
      const dayName = shipDay.weekday;
      return `Order by ${formatCutoffLabel(cutoffHour)} ${dayName} to ship ${dayName}`;
    }

    const shipDay = getNextShippingDayAfter(parts.year, parts.month, parts.day, holidays);
    if (!shipDay) return '';

    const targetMs = easternTimestamp(shipDay.year, shipDay.month, shipDay.day, cutoffHour, 0, 0);
    const sec = secondsUntilTarget(targetMs, now);
    const tomorrow = addCalendarDays(parts.year, parts.month, parts.day, 1);
    const isCalendarTomorrow =
      shipDay.year === tomorrow.year && shipDay.month === tomorrow.month && shipDay.day === tomorrow.day;
    const shipLabel = isCalendarTomorrow ? 'tomorrow' : shipDay.weekday;

    return `Order in the next <span class="green">${formatCountdown(sec)}</span> to ship ${shipLabel}`;
  }

  function updateShippingCountdowns() {
    const message = buildCountdownMessage(new Date(), getConfig());
    if (!message) return;

    document.querySelectorAll('[data-shipping-timer-content]').forEach((el) => {
      el.innerHTML = message;
    });
  }

  function init() {
    if (window.__shippingCountdownInit) return;
    window.__shippingCountdownInit = true;

    updateShippingCountdowns();
    window.setInterval(updateShippingCountdowns, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
